import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Cell } from 'react-table';
import { computeText } from '@/locales/utils';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import type { TableCell, TableRow } from '@/components/atoms/Table/Table.type';
import { getUser, getUserToken } from '@/redux/user';
import { useGetPickupPointsByRestaurantQuery } from '@/services/innovorder/pickupPoint/pickupPoint.endpoints';
import type { PickupPoint } from '@/services/innovorder/pickupPoint/pickupPoint.type';
import { PickupPointTableAction, PickupPointTableActionTypes } from './types';
import { defaultPickupPointAction, PickupPointTableViewModel } from './PickupPoint.type';
import { useRouteMatch } from 'react-router';

export function usePickupPointTableVM(): PickupPointTableViewModel {
    const intl = useIntl();
    const { params } = useRouteMatch<{ brandId: string; restaurantId: string }>();
    const brandId = Number.parseInt(params.brandId, 10);
    const restaurantId = params.restaurantId?.length ? Number.parseInt(params.restaurantId, 10) : undefined;
    const user = useSelector(getUser);
    const userAccessFlags = user?.accessFlags;
    const token = useSelector(getUserToken);
    const { data: pickupPointsData, isLoading } = useGetPickupPointsByRestaurantQuery(
        { restaurantId: restaurantId!.toString(), token: token as string },
        { skip: !restaurantId || !token },
    );

    const [action, setAction] = useState<PickupPointTableAction>(defaultPickupPointAction);

    const toggleCreateModal = (): void => {
        setAction({ ...defaultPickupPointAction, type: 'showCreateModal' });
    };
    const toggleEditModal = (pickupPointId: number, name: string, address: string, additionalInfo: string): void => {
        setAction({
            pickupPointId,
            name,
            type: 'showEditModal',
            address,
            additionalAddressInfo: additionalInfo,
        });
    };

    const handleAction = React.useCallback(
        (
            actionType: PickupPointTableActionTypes,
            { pickupPointId: actionPickupPointId, name: actionName, address, additionalInfo }: PickupPoint,
        ) => {
            setAction({
                pickupPointId: actionPickupPointId,
                name: actionName,
                type: actionType,
                address,
                additionalAddressInfo: additionalInfo,
            });
        },
        [],
    );

    const handleRowClick = React.useCallback((cell: Cell<TableRow, TableCell<unknown>> | undefined) => {
        if (!cell || cell.column.id === 'action') return;
        const row = cell.row;
        const name = row.allCells[0].value.value.text;
        const pickupPointId = row.allCells[0].value.value.pickupPointId;
        const address = row.allCells[1].value.value.text;
        const additionalAddressInfo = row.allCells[2].value.value.text;

        setAction({
            pickupPointId,
            name,
            type: 'showEditModal',
            address,
            additionalAddressInfo,
        });
    }, []);

    const headers = React.useMemo(
        () => [
            {
                Header: computeText(intl, 'pickupPoint.modal.pickupPoint.name'),
                accessor: 'name',
                width: '30%',
            },
            {
                Header: computeText(intl, 'pickupPoint.modal.pickupPoint.address'),
                accessor: 'address',
                width: '30%',
            },
            {
                Header: computeText(intl, 'pickupPoint.modal.pickupPoint.additionalAddressInfo'),
                accessor: 'additionalInfo',
                width: '30%',
            },
            {
                Header: computeText(intl, 'action.action'),
                accessor: 'action',
                width: '10%',
                disableSortBy: true,
            },
        ],
        [intl],
    );

    const rows: TableRow[] = React.useMemo(
        () =>
            pickupPointsData?.map((pickupPoint: PickupPoint) => ({
                name: {
                    type: 'string',
                    value: {
                        text: pickupPoint.name,
                        pickupPointId: pickupPoint.pickupPointId,
                    },
                },
                address: {
                    type: 'string',
                    value: {
                        text: pickupPoint.address,
                        pickupPointId: pickupPoint.pickupPointId,
                    },
                },
                additionalInfo: {
                    type: 'string',
                    value: {
                        text: pickupPoint.additionalInfo,
                        pickupPointId: pickupPoint.pickupPointId,
                    },
                },
                action: {
                    type: 'dropdownText',
                    value: {
                        type: 'text',
                        button: <SVGIcon icon={SVGIcons.MORE_VERTICAL} size={16} />,
                        align: 'end',
                        withPortal: true,
                        items: [
                            {
                                value: 1,
                                label: computeText(intl, 'action.edit'),
                                onClick: () =>
                                    setAction({
                                        pickupPointId: pickupPoint.pickupPointId,
                                        name: pickupPoint.name,
                                        type: 'showEditModal',
                                        address: pickupPoint.address,
                                        additionalAddressInfo: pickupPoint.additionalInfo,
                                    }),
                                isActive: true,
                            },
                            {
                                value: 2,
                                label: computeText(intl, 'action.delete'),
                                onClick: () =>
                                    setAction({
                                        pickupPointId: pickupPoint.pickupPointId,
                                        name: pickupPoint.name,
                                        type: 'showDeleteModal',
                                        address: pickupPoint.address,
                                        additionalAddressInfo: pickupPoint.additionalInfo,
                                    }),
                                isActive: true,
                            },
                        ],
                    },
                },
            })) ?? [],
        [pickupPointsData, intl, handleAction],
    );

    return {
        headers,
        rows,
        action,
        brandId,
        restaurantId,
        isLoading,
        handleRowClick,
        clearAction: () => setAction(defaultPickupPointAction),
        toggleCreateModal,
        toggleEditModal,
        userAccessFlags,
    };
}
